/**
 * The one-time notice in the cloud browser (AMO-152): this browser carries
 * the person's saved logins, so what they sign into here is remembered for
 * their other chats. Shown only while the server says this browser carries
 * them and until the person dismisses it; "Manage" opens Settings → Security.
 *
 * It renders in both places a chat's browser appears: the right panel (a line
 * under the address bar) and the floating player (a compact strip along its
 * bottom edge, inside the card). Dismissing it in one hides it in the other at
 * once; the server remembers it for next time.
 *
 * @module components/preview/SavedLoginsNotice
 */
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useSyncExternalStore } from "react";

import { orgUrlSegment } from "../../orgRouting";
import { fetchBrowserLogins, updateBrowserLoginSettings } from "../../state/browserLogins";
import { useActiveOrgId } from "../../state/orgs";

/** Organisations whose notice was dismissed in this tab, shared by every rendered notice. */
const dismissedOrganizations = new Set<string>();
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const markDismissed = (organizationId: string) => {
  dismissedOrganizations.add(organizationId);
  for (const listener of listeners) listener();
};

export function SavedLoginsNotice({
  carriesSavedLogins,
  variant = "panel",
}: {
  carriesSavedLogins: boolean;
  /** `floating`: the compact strip inside the floating player. */
  variant?: "panel" | "floating";
}) {
  const organizationId = useActiveOrgId();
  const navigate = useNavigate();
  const [unseen, setUnseen] = useState(false);
  const dismissed = useSyncExternalStore(
    subscribe,
    () => organizationId !== null && dismissedOrganizations.has(organizationId),
  );

  useEffect(() => {
    if (!carriesSavedLogins || !organizationId) {
      setUnseen(false);
      return;
    }
    let cancelled = false;
    void fetchBrowserLogins(organizationId).then((logins) => {
      if (!cancelled) {
        setUnseen(logins !== null && logins.enabled && logins.on && !logins.noticeSeen);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [carriesSavedLogins, organizationId]);

  if (!unseen || dismissed || !organizationId) return null;

  const dismiss = () => {
    markDismissed(organizationId);
    void updateBrowserLoginSettings(organizationId, { noticeSeen: true });
  };
  const manage = () => {
    dismiss();
    void navigate({
      to: "/$org/settings/$section",
      params: { org: orgUrlSegment(organizationId), section: "security" },
      hash: "browser-logins",
    } as never);
  };

  if (variant === "floating") {
    return (
      <div
        role="status"
        className="pointer-events-auto absolute inset-x-0 bottom-0 z-[32] flex items-center gap-2 rounded-b-[inherit] border-t border-border/80 bg-popover/95 px-2.5 py-1.5 text-[11px] leading-tight text-muted-foreground backdrop-blur-xl"
      >
        <p className="min-w-0 flex-1">Logins here are remembered for your other private chats.</p>
        <button
          type="button"
          className="shrink-0 font-medium text-foreground underline-offset-2 hover:underline"
          onClick={manage}
        >
          Manage
        </button>
        <button
          type="button"
          className="shrink-0 font-medium text-foreground underline-offset-2 hover:underline"
          onClick={dismiss}
        >
          Got it
        </button>
      </div>
    );
  }

  return (
    <div
      role="status"
      className="flex items-start gap-3 border-b border-border bg-muted/60 px-3 py-2 text-xs text-muted-foreground"
    >
      <p className="min-w-0 flex-1">
        Sites you sign into here are remembered, so your other private chats are already signed in.
        Only you can use them.
      </p>
      <button
        type="button"
        className="shrink-0 font-medium text-foreground underline-offset-2 hover:underline"
        onClick={manage}
      >
        Manage
      </button>
      <button
        type="button"
        className="shrink-0 font-medium text-foreground underline-offset-2 hover:underline"
        onClick={dismiss}
      >
        Got it
      </button>
    </div>
  );
}

/** Test seam: forget dismissals between tests. */
export const resetSavedLoginsNoticeForTests = () => dismissedOrganizations.clear();
